import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

interface PublicLeague {
  id: number;
  name: string;
  venue: string;
  city: string | null;
  country: string | null;
  start_date: string;
  end_date: string;
  organizer_name: string;
}

const fmtDate = (d: string) =>
  new Date(d).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });

const PublicSideActionList: React.FC = () => {
  const navigate = useNavigate();
  const [leagues, setLeagues] = useState<PublicLeague[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');

  useEffect(() => {
    axios.get(`${API_URL}/public/side-action-leagues`)
      .then(r => setLeagues(r.data || []))
      .catch(() => setError('Failed to load leagues.'))
      .finally(() => setLoading(false));
  }, []);

  const filtered = leagues.filter(l => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      l.name.toLowerCase().includes(q) ||
      l.venue.toLowerCase().includes(q) ||
      (l.city ?? '').toLowerCase().includes(q) ||
      (l.country ?? '').toLowerCase().includes(q) ||
      l.organizer_name.toLowerCase().includes(q)
    );
  });

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
      {/* Header */}
      <div className="bg-gray-900/80 backdrop-blur-lg border-b border-yellow-500/20 sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center gap-4">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-700/60 hover:bg-gray-700 text-gray-300 hover:text-white text-sm rounded-lg transition-colors flex-shrink-0"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="text-white font-bold text-xl leading-tight">Live Side Action Results</h1>
            <p className="text-yellow-400/80 text-xs mt-0.5">Public • Read-only</p>
          </div>
          <div className="hidden sm:flex items-center gap-1.5 text-xs text-gray-400">
            <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse inline-block"></span>
            Live
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-8 space-y-6">
        {/* Search bar */}
        <div className="relative">
          <svg className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search by league name, venue, city or organizer…"
            className="w-full pl-10 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-yellow-400/50 focus:bg-white/8 transition text-sm"
          />
          {search && (
            <button
              onClick={() => setSearch('')}
              className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white transition"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>

        {/* States */}
        {loading && (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-yellow-400"></div>
          </div>
        )}

        {!loading && error && (
          <div className="text-center py-20">
            <p className="text-red-400">{error}</p>
            <button onClick={() => window.location.reload()} className="mt-4 text-sm text-gray-400 hover:text-white transition underline">
              Try again
            </button>
          </div>
        )}

        {!loading && !error && filtered.length === 0 && (
          <div className="text-center py-20">
            <p className="text-4xl mb-4">🎳</p>
            <p className="text-gray-400 text-lg font-medium">
              {search ? 'No leagues match your search.' : 'No active side action leagues found.'}
            </p>
            {search && (
              <button onClick={() => setSearch('')} className="mt-3 text-sm text-yellow-400 hover:text-yellow-300 transition underline">
                Clear search
              </button>
            )}
          </div>
        )}

        {!loading && !error && filtered.length > 0 && (
          <>
            <p className="text-xs text-gray-500">
              {filtered.length} league{filtered.length !== 1 ? 's' : ''}{search ? ' found' : ' active'}
            </p>
            <div className="grid gap-4 sm:grid-cols-2">
              {filtered.map(league => (
                <button
                  key={league.id}
                  onClick={() => navigate(`/live/side-action/${league.id}`)}
                  className="group text-left bg-white/5 hover:bg-white/10 border border-white/10 hover:border-yellow-400/40 rounded-2xl p-5 transition-all duration-200 hover:shadow-lg hover:shadow-yellow-500/5"
                >
                  {/* League name + live badge */}
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <h2 className="text-white font-bold text-lg leading-tight group-hover:text-yellow-300 transition-colors">
                      {league.name}
                    </h2>
                    <span className="flex-shrink-0 flex items-center gap-1 text-[10px] font-semibold bg-green-500/20 text-green-400 border border-green-500/30 px-2 py-0.5 rounded-full mt-0.5">
                      <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse"></span>
                      Live
                    </span>
                  </div>

                  {/* Details grid */}
                  <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm mb-4">
                    <div>
                      <div className="text-[10px] text-gray-500 uppercase tracking-wide font-semibold mb-0.5">Venue</div>
                      <div className="text-gray-200 truncate">{league.venue}</div>
                    </div>
                    {(league.city || league.country) && (
                      <div>
                        <div className="text-[10px] text-gray-500 uppercase tracking-wide font-semibold mb-0.5">Location</div>
                        <div className="text-gray-200 truncate">
                          {[league.city, league.country].filter(Boolean).join(', ')}
                        </div>
                      </div>
                    )}
                    <div>
                      <div className="text-[10px] text-gray-500 uppercase tracking-wide font-semibold mb-0.5">Starts</div>
                      <div className="text-gray-200">{fmtDate(league.start_date)}</div>
                    </div>
                    <div>
                      <div className="text-[10px] text-gray-500 uppercase tracking-wide font-semibold mb-0.5">Ends</div>
                      <div className="text-gray-200">{fmtDate(league.end_date)}</div>
                    </div>
                  </div>

                  {/* Organizer */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 rounded-full bg-yellow-500/20 border border-yellow-500/30 flex items-center justify-center">
                        <span className="text-yellow-400 text-xs font-bold">
                          {league.organizer_name.charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div>
                        <div className="text-[10px] text-gray-500">Organized by</div>
                        <div className="text-yellow-300 text-xs font-medium">{league.organizer_name}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 text-xs text-gray-500 group-hover:text-yellow-400 transition-colors">
                      View Results
                      <svg className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PublicSideActionList;
